import { useContext, useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, IRootState } from '../../redux/rootReducer';
import Select from 'react-select';
import moment from 'moment';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import { AppContext } from '../context/AppContect';
import {
    deactivateAccountRequest,
    deleteAccountRequest,
    editProfile,
    resetProfileMessage,
} from '../../redux/auth/actions/editProfile.action';
import Toast from '../toast/Toast';
import axios from 'axios';
import { uploadImages } from '../../redux/auth/actions/auth.action';
import CamIcon from '../../images/icons/cam.svg';
import Avatar from '../../images/avatar-placeholder.jpg';
import drop from '../../images/icons/icon-drop-down.svg';
import Popup from '../common/popup/Popup';
import TinyLoader from '../tiny-loader/TinyLoader';
import CloseIcon from '../../images/icon-close.svg';
import CamLayer from '../../images/cam-layer.svg';
import style from './Profile.module.css';
import MageDotsIcon from '../icons/MageDotsIcon';
import Loader from '../common/loader/Loader';

const bed = [
    { value: 'Studio', label: 'Studio' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
];

const Profile = () => {
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const appContext = useContext(AppContext);
    const { firstName, lastName, countryCode, desiredNumBedrooms, expectedMoveInDate, phoneNumber, email, photo_url } = appContext.visitor;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const today = moment().format('YYYY-MM-DD');

    const profileError = useSelector((state: IRootState) => state.editProfileReducer.errMsg);
    const profileResponse = useSelector((state: IRootState) => state.editProfileReducer.data);
    const profileLoading = useSelector((state: IRootState) => state.editProfileReducer?.loading);
    const [showWebCam, setShowWebCam] = useState(false);
    const [uploadFiles, setUploadFiles] = useState('');
    const [newProfile, setNewProfile] = useState('');
    const [hasMediaAccess, setHasMediaAccess] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('error');
    const webcamRef = useRef<Webcam>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState<null | string>(null);
    const [phoneNumberLength, setPhoneNumberLength] = useState(11);
    const [dialCode, setDialCode] = useState('1');
    const dropdownRef = useRef<HTMLDivElement>(null);

    const videoConstraints: MediaTrackConstraints | boolean = {
        facingMode: 'user', // or 'environment' for rear camera
    };

    const capturePhoto = () => {
        const imageSrc = webcamRef.current?.getScreenshot();
        if (imageSrc) {
            setNewProfile(imageSrc);
        }
    };

    const {
        handleSubmit,
        getValues,
        formState: { isValid, errors, isDirty },
        control,
        reset,
    } = useForm({
        defaultValues: {
            email: '',
            phoneNumber: '',
            firstName: '',
            lastName: '',
            countryCode: 1,
            desiredNumBedrooms: '',
            expectedMoveInDate: '',
            oldExpectedMoveInDate: '',
        },
        mode: 'onChange',
    });

    useEffect(() => {
        if (appContext?.visitor) {
            const formattedDate = expectedMoveInDate
                ? moment(expectedMoveInDate).format('YYYY-MM-DD')
                : '';

            reset({
                firstName: firstName,
                lastName: lastName,
                expectedMoveInDate: formattedDate,
                oldExpectedMoveInDate: formattedDate,
                phoneNumber: countryCode + phoneNumber,
                desiredNumBedrooms: desiredNumBedrooms,
                email: email,
            });
            setUploadFiles(photo_url);
            setNewProfile('');
        }
    }, [appContext]);

    useEffect(() => {
        if (profileError !== '') {
            setTimeout(() => {
                resetProfileMessage();
            }, 4000);
        }
    }, [profileError]);

    const onSuccess = async () => {
        try {
            const response = await axios.get('/visitor/me', {
                headers: { 'Cache-Control': 'no-cache' },
            });
            appContext.visitor = response?.data?.visitor;
            localStorage.setItem('visitor', JSON.stringify(response?.data?.visitor));

            const { firstName, lastName, countryCode, desiredNumBedrooms, expectedMoveInDate, phoneNumber, email, photo_url } =
                response?.data?.visitor;
            const formattedDate = expectedMoveInDate
                ? moment(expectedMoveInDate).format('YYYY-MM-DD')
                : '';

            reset({
                firstName: firstName,
                lastName: lastName,
                expectedMoveInDate: formattedDate,
                oldExpectedMoveInDate: formattedDate,
                phoneNumber: countryCode + phoneNumber,
                desiredNumBedrooms: desiredNumBedrooms,
                email: email,
            });
            setUploadFiles(photo_url);
            setNewProfile('');
            setIsLoading(false);
        } catch (error) {
            console.log(error);
        }
    };

    const base64ToFile = (base64String: string, fileName: string, contentType: string): File => {
        const trimmedBase64String = base64String.trim();

        if (!trimmedBase64String.startsWith('data:image/jpeg;base64,')) {
            throw new Error('Invalid base64 string format');
        }
        const byteCharacters = atob(trimmedBase64String.split(',')[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: contentType });
        return new File([blob], fileName, { type: contentType });
    };

    const uploadProfile = async () => {
        if (uploadFiles !== photo_url) {
            const file = base64ToFile(uploadFiles, 'user', 'image/jpeg');
            const response = await uploadImages(file, '/user-profile');
            return response;
        } else {
            return photo_url;
        }
    };

    const onSubmit = async (data: {
        firstName: string;
        lastName: string;
        expectedMoveInDate: string;
        phoneNumber: string;
        desiredNumBedrooms: string;
        email: string;
    }) => {
        setIsLoading(true);
        const response = await uploadProfile();
        const profileDetails = {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            countryCode: 1,
            phoneNumber: Number(data.phoneNumber.toString().substring(1)),
            expectedMoveInDate: data?.expectedMoveInDate,
            desiredNumBedrooms: data.desiredNumBedrooms,
            photo_url: response,
        };
        dispatch(editProfile(profileDetails, setToastType, onSuccess, setIsLoading));
    };

    const requestCameraPermission = async (): Promise<boolean> => {
        if (!navigator?.mediaDevices?.getUserMedia) {
            setToastType('error');
            setToastMessage('Camera access is not supported on this device.');
            setHasMediaAccess(false);
            return false;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            stream.getTracks().forEach((track) => track.stop());
            setHasMediaAccess(true);
            return true;
        } catch (error) {
            setHasMediaAccess(false);
            if (error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')) {
                setToastType('error');
                setToastMessage('Camera access denied! Please grant access to your camera to continue.');
            } else {
                setToastType('error');
                setToastMessage('Unable to access the camera. Please try again.');
            }
            return false;
        }
    };

    const handleCameraClick = async () => {
        if (!hasMediaAccess) {
            const granted = await requestCameraPermission();
            if (!granted) {
                return;
            }
        }

        setShowWebCam(true);
    };

    useEffect(() => {
        if (toastMessage) {
            setTimeout(() => {
                setToastMessage('');
                setToastType('');
            }, 4000);
        }
    }, [toastMessage]);

    useEffect(() => {
        dispatch(resetProfileMessage());
    }, [dispatch]);

    const submitPhoto = () => {
        const newUserProfile = newProfile;
        setUploadFiles(newUserProfile);
        setNewProfile('');
        setShowWebCam(false);
    };

    const ModalBody = () => {
        return (
            <div className={style.modalBodyContainer}>
                <div className={style.modalHeader}>
                    <h3>Upload Selfie</h3>

                    <div
                        className={style.closeIcon}
                        onClick={() => {
                            setShowWebCam(false);
                            setNewProfile('');
                        }}>
                        <img src={CloseIcon} alt="close-icon" width={20} height={20} />
                    </div>
                </div>
                <div className={style.modalBodyInnerContainer}>
                    {!newProfile && (
                        <>
                            <div className={style.camLayer}>
                                <img src={CamLayer} alt="layer" width={684} height={480} />
                            </div>
                            <Webcam
                                audio={false}
                                className={style.webCamContainer}
                                screenshotFormat="image/jpeg"
                                videoConstraints={videoConstraints}
                                ref={webcamRef}
                                autoFocus
                            />
                        </>
                    )}

                    {newProfile && (
                        <div className={style.capturedImage}>
                            <img src={newProfile} alt="" width={640} height={480} />
                        </div>
                    )}
                </div>
                <div className={style.modalFooter}>
                    {newProfile && (
                        <button className={style.retakeBtn} onClick={() => setNewProfile('')}>
                            Recapture Photo
                        </button>
                    )}
                    <button className={newProfile ? style.submitBtn : style.captureBtn} onClick={newProfile ? submitPhoto : capturePhoto}>
                        {!!newProfile ? 'Submit my picture' : 'Capture photo'}
                    </button>
                </div>
            </div>
        );
    };

    const onDeleteRequestSuccess = (message: string) => {
        setToastType('success');
        setIsLoading(false);
        setToastMessage(message);
    };

    const onDeleteRequestFailure = (message: string) => {
        setToastType('error');
        setIsLoading(false);
        setToastMessage(message);
    };

    const onDeleteRequest = (reason: string) => {
        setIsLoading(true);
        dispatch(deleteAccountRequest(reason, onDeleteRequestSuccess, onDeleteRequestFailure));
    };

    const onDeactivateRequestSuccess = (message: string) => {
        setToastType('success');
        setIsLoading(false);
        setToastMessage(message);
    };

    const onDeactivateRequestFailure = (message: string) => {
        setToastType('error');
        setIsLoading(false);
        setToastMessage(message);
    };

    const onDeactivateRequest = () => {
        setIsLoading(true);
        dispatch(deactivateAccountRequest(onDeactivateRequestSuccess, onDeactivateRequestFailure));
    };

    const ActionModalBody = () => {
        const [reason, setReason] = useState<null | string>(null);

        return (
            <div className={style.actionModal}>
                <h2 className={style.title}>{`Are you sure you want to ${showConfirmModal?.toString()?.toLowerCase() === 'delete' ? 'delete' : 'deactivate'
                    } your account?`}</h2>
                <p className={style.description}>
                    {showConfirmModal?.toString()?.toLowerCase() === 'delete'
                        ? `Once deleted, all your appointments will be removed, and this action cannot be undone.`
                        : 'Once your account is deactivated, you will no longer be able to log in, create appointments, or access Pineapple services.'}
                </p>
                {showConfirmModal?.toString().toLowerCase() === 'delete' && (
                    <textarea
                        className={style.textArea}
                        placeholder="Enter Reason"
                        value={reason || ''}
                        maxLength={200}
                        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                            setReason(event.target.value);
                        }}
                    />
                )}

                <div className={style.actions}>
                    <button disabled={isLoading} className={style.cancelButton} onClick={() => setShowConfirmModal(null)}>
                        Cancel
                    </button>
                    <button
                        disabled={
                            (showConfirmModal?.toString().toLowerCase() === 'delete' && (reason === null || reason.trim() === '')) ||
                            isLoading
                        }
                        className={style.confirmButton}
                        onClick={() => {
                            setShowConfirmModal(null);
                            showConfirmModal?.toString().toLowerCase() === 'delete'
                                ? reason && onDeleteRequest(reason)
                                : onDeactivateRequest();
                        }}>
                        {showConfirmModal?.toString()?.toLowerCase() === 'delete' ? `Delete Account` : 'Deactivate'}
                    </button>
                </div>
            </div>
        );
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const Dropdown = () => {
        const toggleDropdown = () => {
            setIsOpen((prev) => !prev);
        };

        return (
            <div className={style.dropDownWrapper} ref={dropdownRef}>
                <button className={style.settingButton} type="button" onClick={toggleDropdown}>
                    <MageDotsIcon />
                </button>
                <ul className={`${style.menu} ${isOpen ? style.active : ''}`}>
                    <li
                        onClick={() => {
                            setShowConfirmModal('deactivate');
                            setIsOpen(false);
                        }}>
                        Deactivate Account
                    </li>

                    {/* <li onClick={() => {
            setShowConfirmModal('delete');
          }}>Delete Account</li> */}
                </ul>
            </div>
        );
    };

    return (
        <div className={style.profileContainer}>
            {isLoading && <Loader />}
            <div className={style.innerContainer}>
                <form onSubmit={handleSubmit(onSubmit)} className={`${style.formWrapper} outletContainer`} noValidate>
                    <div className={style.formHeader}>
                        <h2 className={style.authTitle}>Edit Profile</h2>
                        <Dropdown />
                    </div>

                    <div className={style.imageUploaderContainer}>
                        <div className={style.imageInnerContainer}>
                            <img
                                src={uploadFiles ? uploadFiles : Avatar}
                                width={120}
                                height={120}
                                alt="avatar"
                                className={style.userAvatar}
                            />
                            <div role="button" className={style.camIconContainer} onClick={handleCameraClick}>
                                <img src={CamIcon} alt="camera-icon" width={20} height={20} />
                            </div>
                        </div>
                    </div>

                    {/* First Name */}
                    <Controller
                        name="firstName"
                        control={control}
                        rules={{
                            required: 'First Name is required',
                        }}
                        render={({ field: { onChange, value } }) => {
                            return (
                                <div className={style.fieldWrapper}>
                                    <label>First Name</label>
                                    <div className={style.passwordWrapper}>
                                        <input
                                            value={value}
                                            onChange={(e) => onChange(e)}
                                            type="text"
                                            id="firstName"
                                            name="firstName"
                                            placeholder="Enter First Name"
                                        />
                                    </div>
                                    {errors?.firstName && <p className={style.errorText}>{errors?.firstName?.message}</p>}
                                </div>
                            );
                        }}
                    />

                    {/* Last Name */}
                    <Controller
                        name="lastName"
                        control={control}
                        rules={{
                            required: 'Last Name is required',
                        }}
                        render={({ field: { onChange, value } }) => {
                            return (
                                <div className={style.fieldWrapper}>
                                    <label>Last Name</label>
                                    <div className={style.passwordWrapper}>
                                        <input
                                            value={value}
                                            type="text"
                                            onChange={onChange}
                                            id="lastName"
                                            name="lastName"
                                            placeholder="Enter Last Name"
                                        />
                                    </div>
                                    {errors?.lastName && <p className={style.errorText}>{errors?.lastName?.message}</p>}
                                </div>
                            );
                        }}
                    />

                    {/* Email */}
                    <Controller
                        name="email"
                        control={control}
                        render={({ field: { value } }) => {
                            return (
                                <div className={style.fieldWrapper}>
                                    <label>Email</label>
                                    <div className={style.passwordWrapper}>
                                        <input
                                            value={value}
                                            type="email"
                                            disabled
                                            id="email"
                                            name="email"
                                            placeholder="Enter your email address"
                                        />
                                    </div>
                                </div>
                            );
                        }}
                    />

                    {/* Phone Number */}
                    <Controller
                        name="phoneNumber"
                        control={control}
                        rules={{
                            required: 'Phone number should not be empty',
                        }}
                        render={({ field: { onChange, value } }) => {
                            return (
                                <div className={`${style.fieldWrapper} ${style.sm} ${style.phoneNum}`}>
                                    <label>Phone Number</label>
                                    <PhoneInput
                                        country={'us'}
                                        value={value}
                                        inputClass="phone-input"
                                        onChange={(value: string, data: CountryData) => {
                                            setPhoneNumberLength(data?.format?.length);
                                            onChange(value);
                                            setDialCode(data?.dialCode);
                                        }}
                                        inputStyle={{
                                            marginLeft: '30px',
                                            maxWidth: 'calc(100% - 30px)',
                                        }}
                                        inputProps={{
                                            required: true,
                                            maxLength: phoneNumberLength,
                                        }}
                                        enableSearch
                                        copyNumbersOnly
                                        countryCodeEditable={false}
                                        enableLongNumbers={true}
                                    />
                                    {errors?.phoneNumber && <p className={style.errorText}>{errors?.phoneNumber?.message}</p>}
                                </div>
                            );
                        }}
                    />

                    {/* Expected Move in date */}
                    <Controller
                        name="expectedMoveInDate"
                        control={control}
                        defaultValue=""
                        rules={{
                            required: 'Move in date should not be empty',
                            validate: (value) => {
                                const selectedDate = new Date(value);
                                const oldExpectedMoveInDate = new Date(getValues('oldExpectedMoveInDate'));
                                if (
                                    moment(moment(selectedDate).format('YYYY-MM-DD')).isSame(
                                        moment(oldExpectedMoveInDate).format('YYYY-MM-DD'),
                                    )
                                ) {
                                    return true;
                                } else if (
                                    moment(moment(selectedDate).format('YYYY-MM-DD')).isSameOrAfter(
                                        moment(new Date(today)).format('YYYY-MM-DD'),
                                    )
                                ) {
                                    return true;
                                } else {
                                    return 'Move in date should be the old expected move-in date, today or later';
                                }
                            },
                        }}
                        render={({ field: { onChange, value } }) => {
                            return (
                                <div className={style.fieldWrapper}>
                                    <label>
                                        Expected Move In Date <sup>*</sup>
                                    </label>
                                    <input
                                        onChange={(e) => {
                                            onChange(e.target.value);
                                        }}
                                        type="date"
                                        name="expectedMoveInDate"
                                        max="9999-12-31"
                                        placeholder="YYYY-MM-DD"
                                        value={value}
                                        min={today}
                                    />
                                    {errors?.expectedMoveInDate && <p className={style.errorText}>{errors?.expectedMoveInDate?.message}</p>}
                                </div>
                            );
                        }}
                    />

                    {/* Bedrooms */}
                    <Controller
                        name="desiredNumBedrooms"
                        control={control}
                        rules={{
                            required: 'Last Name is required',
                        }}
                        render={({ field: { onChange, value } }) => {
                            return (
                                <div className={`${style.fieldWrapper} ${style.dropDownOption}`}>
                                    <label>
                                        Desired Number of Bedrooms<sup>*</sup>
                                    </label>
                                    <div className="profile-custom-dropdown">
                                        <Select
                                            onChange={(selectedOption) => {
                                                onChange(selectedOption?.value);
                                            }}
                                            // isSearchable={false}

                                            classNamePrefix="custom-profile-dropdown"
                                            options={bed}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menuPortal: (base) => ({ ...base, zIndex: 9, fontSize: 14, fontWeight: 400 }),
                                                menuList: (base) => ({
                                                    ...base,
                                                    maxHeight: '124px',
                                                    height: '100%',
                                                }),
                                                option: (base) => ({ ...base, cursor: 'pointer' }),
                                            }}
                                            placeholder={<div className="selectPlaceholderText">Select</div>}
                                            value={value ? { value: value, label: value } : null}
                                        />
                                        <span className="custom-dropdown-indicator">
                                            <img src={drop} alt="dropdown icon" />
                                        </span>
                                    </div>
                                </div>
                            );
                        }}
                    />

                    <div className={style.buttonContainer}>
                        <div className={style.cancelWrapper} onClick={() => navigate('/appointments')}>
                            <button type="button">Cancel</button>
                        </div>

                        <div className={style.submitWrapper}>
                            <button
                                type="submit"
                                disabled={(isValid && !isDirty && photo_url === uploadFiles) || profileLoading || isLoading}>
                                {
                                    <span className={isLoading || profileLoading ? style.loader : ''}>
                                        {profileLoading || isLoading ? <TinyLoader /> : 'Update'}
                                    </span>
                                }
                            </button>
                        </div>
                    </div>
                </form>
            </div>
            <Popup open={showWebCam} setOpen={setShowWebCam} bodyData={<ModalBody />} />
            <Popup
                className="terms-modal"
                open={showConfirmModal !== null ? true : false}
                bodyData={<ActionModalBody />}
                onCloseModal={() => setShowConfirmModal(null)}
            />
            {(profileError !== '' || profileResponse?.statusCode === 200 || toastMessage) && (
                <Toast
                    message={profileError || profileResponse?.message || toastMessage}
                    type={toastType === 'error' || profileError ? 'error' : 'success'}
                />
            )}
        </div>
    );
};

export default Profile;
